import { CloseOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, message, Popconfirm, Space, Input, TimePicker, Select, DatePicker, Image } from "antd";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { apiUrl, serverFetch } from "@server";
import { strDate2Server } from "@extensions/convert";
import dayjs from "dayjs";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import { statusTag } from "@extensions/design";
import "../company/company.css";
import "./records.css";

const planImageSrc = url =>
  `${String(apiUrl || "").replace(/\/$/, "")}/api/Image/${encodeURIComponent(url)}`;

const Record = ({ id, defaultItem, onClose, users }) => {
  const viewport = useViewportBreakpoint();
  const isMobile = viewport === "mobile";
  const [item, setItem] = useState({ ...defaultItem });
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const datePickerRef = useRef(null);
  const [dateControlWidth, setDateControlWidth] = useState(null);

  useLayoutEffect(() => {
    const native = datePickerRef.current?.nativeElement;
    if (!native) return undefined;
    const measure = () => {
      const w = native.offsetWidth;
      if (w > 0) setDateControlWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(native);
    return () => ro.disconnect();
  }, [isMobile, id, loading]);

  const onValuesChange = value => setItem({ ...item, ...value });
  const onCancel = () => onClose(false);

  const onSave = () => {
    const newItem = { ...item, docDate: strDate2Server(item.docDate), datePlan: strDate2Server(item.datePlan) };
    if (item.startDt && item.datePlan) {
      const date = item.datePlan;
      const startTime = newItem.startDt.format("HH:mm");
      newItem.startDt = dayjs(`${date.format("YYYY-MM-DD")}T${startTime}`).format("YYYY-MM-DDTHH:mm") + ":00.000Z";
    } else newItem.startDt = null;
    if (item.finishDt && item.datePlan) {
      const date = item.datePlan;
      const finishTime = newItem.finishDt.format("HH:mm");
      newItem.finishDt = dayjs(`${date.format("YYYY-MM-DD")}T${finishTime}`).format("YYYY-MM-DDTHH:mm") + ":00.000Z";
    } else newItem.finishDt = null;
    setLoading(true);
    serverFetch("plan/doc", { method: id ? "PUT" : "POST", bodyData: newItem })
      .then(() => onClose(true))
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const onDelete = () => {
    setLoading(true);
    serverFetch(`plan/doc/${id}`, { method: "DELETE" })
      .then(() => onClose(true))
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const getData = () => {
    if (id) {
      setLoading(true);
      serverFetch(`plan/doc/${id}`)
        .then(data => {
          const data2 = {
            ...data,
            docDate: data.docDate ? dayjs(data.docDate) : null,
            datePlan: data.datePlan ? dayjs(data.datePlan) : null,
            startDt: data.startDt ? dayjs(data.startDt) : null,
            finishDt: data.finishDt ? dayjs(data.finishDt) : null,
            files: Array.isArray(data.files) ? data.files : [],
          };
          setLoading(false);
          setItem(data2);
          form.setFieldsValue(data2);
        })
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        });
    } else {
      const initial = { ...defaultItem, cnts: [], files: Array.isArray(defaultItem?.files) ? defaultItem.files : [] };
      form.setFieldsValue(initial);
      setItem(initial);
    }
  };

  useEffect(getData, [id, form, defaultItem]);

  const formProps = isMobile
    ? { layout: "vertical", colon: false, disabled: loading, style: { width: "100%", maxWidth: "100%" } }
    : { labelCol: { span: 6 }, wrapperCol: { span: 24 }, colon: false, disabled: loading };

  const files = item.files ?? [];
  const filesGallery =
    files.length > 0 ? (
      <Image.PreviewGroup>
        <div className="record-files-gallery">
          {files.map((file, index) => (
            <figure key={file.fileId ?? file.url ?? index} className="record-files-gallery-item">
              <Image
                src={planImageSrc(file.url)}
                alt={file.url || ""}
                width="100%"
                height={120}
                style={{ objectFit: "cover" }}
              />
              <figcaption className="record-files-gallery-caption">
                <span className="record-files-gallery-id">{file.fileId != null ? `#${file.fileId}` : "—"}</span>
                <span className="record-files-gallery-url" title={file.url}>
                  {file.url}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Image.PreviewGroup>
    ) : null;

  const formFields = (
    <>
      <Form.Item name="datePlan" label="Дата">
        <DatePicker
          ref={datePickerRef}
          value={item.datePlan}
          onChange={date => setItem({ ...item, datePlan: date })}
          format="DD.MM.YYYY"
        />
      </Form.Item>
      <Form.Item name="startDt" label="Время начала">
        <TimePicker
          format="HH:mm"
          style={dateControlWidth ? { width: dateControlWidth, maxWidth: "100%" } : undefined}
        />
      </Form.Item>
      <Form.Item name="finishDt" label="Время окончания">
        <TimePicker
          format="HH:mm"
          style={dateControlWidth ? { width: dateControlWidth, maxWidth: "100%" } : undefined}
        />
      </Form.Item>
      <Form.Item name="predName" label="Клиент">
        <Input value={item.predName} onChange={e => setItem({ ...item, predName: e.target.value })} />
      </Form.Item>
      <Form.Item name="predAddress" label="Адрес">
        <Input value={item.predAddress} onChange={e => setItem({ ...item, predAddress: e.target.value })} />
      </Form.Item>
      <Form.Item name="predPhone" label="Телефон">
        <Input value={item.predPhone} onChange={e => setItem({ ...item, predPhone: e.target.value })} />
      </Form.Item>
      <Form.Item name="userId" label="Исполнитель">
        <Select
          value={item.userId}
          onChange={value => setItem({ ...item, userId: value })}
          placeholder="Выберите исполнителя"
        >
          {users?.map(user => (
            <Select.Option key={user.id} value={user.id}>
              {user.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name="comment" label="Комментарий">
        <Input.TextArea value={item.comment} onChange={e => setItem({ ...item, comment: e.target.value })} rows={3} />
      </Form.Item>
      <Form.Item name="osn" label="Основание">
        <Input value={item.osn} onChange={e => setItem({ ...item, osn: e.target.value })} />
      </Form.Item>
      {!isMobile && (
        <Form.Item label=" ">
          <Space>
            <Popconfirm title="Подтверждение" description="Удалить?" onConfirm={onDelete}>
              <Button className="sport-btn" disabled={!id}>
                Удалить
              </Button>
            </Popconfirm>
            <Button className="sport-btn" onClick={onSave}>
              Сохранить
            </Button>
            <Button className="sport-btn" onClick={onCancel}>
              Закрыть
            </Button>
          </Space>
        </Form.Item>
      )}
    </>
  );

  return (
    <div>
      <div
        className={`misoft-modal-overlay${isMobile ? " company-base-overlay--mobile" : ""}`}
        onClick={e => e.target === e.currentTarget && onCancel()}
      >
        <div
          className={`misoft-modal-content${isMobile ? " company-base-sheet--mobile" : ""}`}
          style={isMobile ? undefined : { width: 800 }}
          onClick={e => e.stopPropagation()}
        >
          {isMobile ? (
            <>
              <div className="company-base-sheet-header">
                <span className="company-base-sheet-title">Запись</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {statusTag(item.status)}
                  <Space size="small" className="company-base-sheet-actions">
                  <Popconfirm title="Подтверждение" description="Удалить?" onConfirm={onDelete}>
                    <Button
                      type="default"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!id || loading}
                      aria-label="Удалить"
                      className="company-base-header-icon-btn"
                    />
                  </Popconfirm>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={onSave}
                    loading={loading}
                    aria-label="Сохранить"
                    className="company-base-header-icon-btn"
                  />
                  <Button
                    type="default"
                    icon={<CloseOutlined />}
                    onClick={onCancel}
                    aria-label="Закрыть"
                    className="company-base-header-icon-btn"
                  />
                </Space>
                </div>
              </div>
              <div className="company-base-sheet-body">
                <Form form={form} onValuesChange={onValuesChange} {...formProps}>
                  {formFields}
                </Form>
                {filesGallery}
              </div>
            </>
          ) : (
            <>
              <div className="misoft-modal-title" style={{ marginBottom: 0 }}>
                <span>Запись</span>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {statusTag(item.status)}
                  <CloseOutlined
                    className="misoft-section-title-close"
                    onClick={() => onClose()}
                    style={{ zIndex: 9, color: "#2f6f00", fontSize: 16 }}
                  />
                </div>
              </div>
              <Form form={form} onValuesChange={onValuesChange} {...formProps}>
                {formFields}
              </Form>
              {filesGallery}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Record;
